import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import SimplePageHero from '../marketing/components/SimplePageHero';
import ArticleCard from '../marketing/components/ArticleCard';
import { ARTICLES, CATEGORIES, articlesByCategory } from '../marketing/data';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';

const BlogIndexPage: React.FC = () => {
  useMarketingFonts();
  useSeoHead({
    title: 'The MillOS Blog — UK Landlord Compliance, Tax & Accounting',
    description:
      "Compliance rules, tax changes and accounting playbooks for UK landlords — Gas Safety, EPC, EICR, Open Banking, Section 24 and more, written by MillOS's property specialists.",
    ogType: 'website',
    canonicalPath: '/blog',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'The MillOS Blog',
        description: 'UK landlord compliance, tax and accounting articles.',
        url: 'https://www.millos.uk/blog',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.millos.uk/' },
            { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://www.millos.uk/blog' },
          ],
        },
      },
    ],
  });

  return (
    <Box className="lv-marketing" sx={{ background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'hidden' }}>
      <MarketingNav />
      <SimplePageHero
        eyebrow="Blog and articles"
        title={<>The MillOS <Box component="em" sx={{ fontStyle: 'italic' }}>Blog</Box></>}
        body="Compliance rules, tax changes and accounting playbooks for UK landlords — written by our property specialists."
        bottomPadding="40px"
      />

      <Box component="section" sx={{ padding: { xs: '0 16px', md: '0 52px' } }}>
        <Box sx={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
          <Box sx={{ border: '1px solid rgba(0,0,0,0.1)', background: C.inkSoft, color: C.white, borderRadius: '32px', padding: '9px 18px', fontSize: 14 }}>
            All articles
          </Box>
          {CATEGORIES.map((c) => (
            <Box
              key={c.slug}
              component={RouterLink}
              to={`/blog/category/${c.slug}`}
              sx={{ border: '1px solid rgba(0,0,0,0.1)', background: C.white, color: C.ink, borderRadius: '32px', padding: '9px 18px', fontSize: 14, '&:hover': { borderColor: C.accent, color: C.accent } }}
            >
              {c.name} <Box component="span" sx={{ color: C.muted }}>{articlesByCategory(c.slug).length}</Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box component="section" sx={{ padding: { xs: '48px 16px 64px', md: '56px 52px 96px' } }}>
        <Box sx={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3,1fr)' }, gap: '28px' }}>
          {ARTICLES.map((a) => (
            <ArticleCard key={a.slug} article={a} />
          ))}
        </Box>
      </Box>

      <MarketingFooter />
    </Box>
  );
};

export default BlogIndexPage;
